import React, { useState, useMemo } from 'react';

import { dashboardCompanyApis } from '@apis/next/companyApis';
import { FormTitle } from '@components/admin/Forms';
import { Layout } from '@components/admin/Layout';
import { CompanyDisplay } from '@components/admin/members/CompanyDisplay';
import { CompanyFormik } from '@components/admin/members/CompanyFormik';
import { ChecklistDisplay } from '@components/admin/members/MemberDetails/ChecklistDisplay';
import { ChecklistFormik } from '@components/admin/members/MemberDetails/ChecklistFormik';
import { InfoManagement } from '@components/admin/members/MemberDetails/InfoManagement';
import { MemberStaffDisplayEditRow } from '@components/admin/members/MemberDetails/MemberStaffDisplayEditRow';
import { PrincipalDisplay } from '@components/admin/members/PrincipalDisplay';
import { PrincipalFormik } from '@components/admin/members/PrincipalFormik';
import { Table, TableCellPropsUnion } from '@components/Table/Table';
import { agencyStrings } from '@data/agency';
import { strings as membershipFormStrings } from '@data/membershipForm';
import { routePlanServiceNameStrings, routePlanStrings } from '@data/routePlan';
import { useCompanyChecklist } from '@hooks/queries/dashboard/useCompanyChecklist';
import { useCompanyDetail } from '@hooks/queries/dashboard/useCompanyDetail';
import { useCompanyPrincipal } from '@hooks/queries/dashboard/useCompanyPrincipal';
import { useRoutePlanList } from '@hooks/queries/dashboard/useRoutePlanList';
import { useSearch } from '@hooks/useSearch';
import Collapse from '@material-ui/core/Collapse';
import { MembershipFormFields } from '@models/MembershipForm';
import { MemberDetailsContext, MemberDetailsContextValue } from '@pages/admin/members/helpers';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { getBreadcrumb, PageMeta, url } from '@utils/pages';
import { useShallowMemo } from '@utils/useShallowMemo';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: '詳細',
    companyInfo: `客側${membershipFormStrings.companyInfo}`,
    principalInfo: `客側${membershipFormStrings.principalInfo}`,
    companyStaffsInfo: '客側スタッフ情報',
    infoManagement: '管理情報',
    edit: '編集',
    id: '申し込み番号',
    createdAt: '申し込み時間',
    status: '未対応',
    admissionDate: '入会時間',
    assignee: 'CS担当者',
    becomeAssignee: '担当者になる',
    evaluation: '会員総合評価',
    memberLegacyNumber: '会員番号',
    export: 'エクスポート',
    createEvaluationSheet: '評価シートを作成',
    inputLegacyMemberNumber: '会員番号を入力',
    inputLegacyDecisionNumber: '稟議番号を入力',
    evaluationSheetK01: '評価シートK01',
    delegationForm: '通関委任状',
    checklist: '確認事項',
    save: '保存',
    defaultTransactionConditions: '普通',
    proposedTransactionConditions: '相談したい',
    no: 'No.',
    name: '名前',
    cellphone: '携帯電話番号',
    landline: '固定電話番号',
    email: 'メール',
    agencyInfo: '代理店情報',
    agenciesAreNotAssigned: '代理店はまだ指定されていません。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    flex: 1,
    padding: '1.5rem 2rem',
    overflow: 'hidden',
  },
  leftColumn: {
    padding: '1.5rem 2rem',
    ...common.flexColumn,
    flex: 1,
    overflowY: 'scroll',
  },
  rightColumn: {
    padding: '1.5rem 2rem',
    ...common.flexColumn,
    width: 360,
    overflowY: 'scroll',
  },
  formContainer: {
    ...common.flexColumn,
    marginBottom: '1rem',
  },
  managementContainer: {
    ...common.flexColumn,
    border: '1px solid #DDDDDD',
  },
  managementCell: {
    ...common.flexColumn,
    padding: '10px 22px',
    '&:not(:last-child)': {
      borderBottom: '1px solid #DDDDDD',
    },
  },
  keyValueRow: {
    ...common.flexInline,
    ...common.middleText,
    color: theme.palette.grey['700'],
    padding: '0.5rem 0',
  },
  managementTitle: {
    ...common.normalText,
    color: theme.palette.grey['800'],
    fontWeight: 500,
    marginBottom: '0.5rem',
  },
  managementButton: {
    alignSelf: 'center',
    width: 160,
    margin: '0.5rem 0',
  },
  formButton: {
    width: 70,
    margin: '1.5rem 1rem',
  },
  checklistForm: {
    padding: '0.5rem 0.5rem',
  },
  alertDot: {
    height: 10,
    width: 10,
    backgroundColor: theme.palette.error.main,
    marginLeft: 20,
    borderRadius: '50%',
    transform: 'translateY(-1px)',
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  emptyPrompt: {
    ...common.flexCenter,
    ...common.textGray500,
    ...common.middleText,
    margin: '1rem 0',
  },
}));

const searchType = t.type({
  id: h.number().cast(),
  type: h.omittable(t.union([t.literal('member'), t.literal('completionRequest')])),
});

export const MemberDetails: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(searchType);

  const type = search.type ?? 'member';

  const [edittingCompanyInfo, setEdittingCompanyInfo] = useState(false);
  const [edittingPrincipalInfo, setEdittingPrincipalInfo] = useState(false);
  const [edittingChecklist, setEdittingChecklist] = useState(false);
  const [expandCompanyInfo, setExpandCompanyInfo] = useState(true);
  const [expandPrincipalInfo, setExpandPrincipalInfo] = useState(true);
  const [expandChecklist, setExpandChecklist] = useState(true);
  const [expandCompanyStaffsInfo, setExpandCompanyStaffsInfo] = useState(true);
  const [expandAgencyInfo, setExpandAgencyInfo] = useState(true);
  const [editingList, setEditingList] = useState<number[]>([]);

  const companyDetailsQuery = dashboardCompanyApis.useGet('/dashboard/company/:id/detail', {
    params: {
      id: search.id,
    },
  });
  const company = useCompanyDetail(search.id);
  const principal = useCompanyPrincipal(search.id);
  const checklist = useCompanyChecklist(search.id);
  const companyRoutePlanList = useRoutePlanList({
    company_id: search.id,
  });

  const breadcrumb = useMemo(
    () => [
      ...(type === 'member'
        ? getBreadcrumb(url('/admin/members/Members'))
        : getBreadcrumb(url('/admin/members/CompletionRequests'))),
      String(search.id).padStart(9, '0'),
    ],
    [search.id, type],
  );

  const companyStaffsQuery = dashboardCompanyApis.useGet('/dashboard/company/:id/staff', {
    params: {
      id: search.id,
    },
  });

  const CompanyStaffHeadColumns: TableCellPropsUnion[] = [
    {
      label: strings.no,
    },
    {
      label: strings.name,
    },
    {
      label: strings.cellphone,
    },
    {
      label: strings.landline,
    },
    {
      label: strings.email,
    },
    {
      label: '',
    },
  ];

  const agencyHeadColumns: TableCellPropsUnion[] = [
    {
      label: strings.no,
    },
    {
      label: routePlanStrings.serviceName,
    },
    {
      label: routePlanStrings.routeName,
    },
    {
      label: agencyStrings.agency,
    },
  ];

  const agencyRows = companyRoutePlanList.data
    ?.slice()
    .sort((a, b) => a.plan_id - b.plan_id)
    .map((result, index) => {
      return [
        {
          label: index + 1,
        },
        {
          label: routePlanServiceNameStrings[result.service_name],
        },
        {
          label: result.name,
        },
        {
          label: result.agency.name_en_short,
        },
      ];
    });

  const contextValue: MemberDetailsContextValue = useShallowMemo(() => ({
    companyDetails: companyDetailsQuery.data!,
  }));

  return (
    <Layout
      breadcrumb={breadcrumb}
      loading={
        company.isLoading ||
        principal.isLoading ||
        checklist.isLoading ||
        companyStaffsQuery.isLoading ||
        companyDetailsQuery.isLoading
      }
      title={strings.title}
      errors={[
        company.error,
        principal.error,
        checklist.error,
        companyStaffsQuery.error,
        companyDetailsQuery.error,
      ]}
    >
      {() => {
        const companyFormValues = MembershipFormFields.shared.mapCompanyToCompanyForm(
          company.data!,
        );

        const principalFormValues = MembershipFormFields.shared.mapCompanyPrincipalToPrincipalForm(
          principal.data!,
        );

        const checklistFormValues = MembershipFormFields.shared.mapChecklistToChecklistForm(
          checklist.data!,
        );

        return (
          <MemberDetailsContext.Provider value={contextValue}>
            <div className={classes.container}>
              <div className={classes.leftColumn}>
                <FormTitle
                  title={strings.companyInfo}
                  editting={edittingCompanyInfo}
                  onChangeEditting={(editting) => {
                    setEdittingCompanyInfo(editting);
                    if (editting) {
                      setExpandCompanyInfo(true);
                    }
                  }}
                  noExpand={false}
                  expanded={expandCompanyInfo}
                  onChangeExpanded={(expanded) => {
                    setExpandCompanyInfo(expanded);
                    if (!expanded) {
                      setEdittingCompanyInfo(false);
                    }
                  }}
                />
                {edittingCompanyInfo ? (
                  <CompanyFormik
                    initialValues={companyFormValues}
                    onFinish={() => setEdittingCompanyInfo(false)}
                  />
                ) : (
                  <CompanyDisplay values={companyFormValues} expand={expandCompanyInfo} />
                )}

                <FormTitle
                  title={strings.checklist}
                  editting={edittingChecklist}
                  onChangeEditting={(editting) => {
                    setEdittingChecklist(editting);
                    if (editting) {
                      setEdittingChecklist(true);
                    }
                  }}
                  noExpand={false}
                  expanded={expandChecklist}
                  onChangeExpanded={(expanded) => {
                    setExpandChecklist(expanded);
                    if (!expanded) {
                      setEdittingChecklist(false);
                    }
                  }}
                />
                {edittingChecklist ? (
                  <ChecklistFormik
                    initialValues={checklistFormValues}
                    onFinish={() => setEdittingChecklist(false)}
                  />
                ) : (
                  <ChecklistDisplay values={checklistFormValues} expand={expandChecklist} />
                )}

                <FormTitle
                  title={strings.principalInfo}
                  editting={edittingPrincipalInfo}
                  onChangeEditting={(editting) => {
                    setEdittingPrincipalInfo(editting);
                    if (editting) {
                      setExpandPrincipalInfo(true);
                    }
                  }}
                  noExpand={false}
                  expanded={expandPrincipalInfo}
                  onChangeExpanded={(expanded) => {
                    setExpandPrincipalInfo(expanded);
                    if (!expanded) {
                      setEdittingPrincipalInfo(false);
                    }
                  }}
                />
                {edittingPrincipalInfo ? (
                  <PrincipalFormik
                    initialValues={principalFormValues}
                    onFinish={() => setEdittingPrincipalInfo(false)}
                  />
                ) : (
                  <PrincipalDisplay
                    values={principalFormValues}
                    expand={expandPrincipalInfo}
                    emailVerified={principal.data?.email_verified ?? true}
                  />
                )}

                <FormTitle
                  title={strings.companyStaffsInfo}
                  noEdit
                  noExpand={false}
                  expanded={expandCompanyStaffsInfo}
                  onChangeExpanded={(expanded) => {
                    setExpandCompanyStaffsInfo(expanded);
                  }}
                />
                <div className={classes.formContainer}>
                  <Collapse in={expandCompanyStaffsInfo} timeout="auto" unmountOnExit>
                    <Table>
                      <Table.Head>
                        <Table.Row columns={CompanyStaffHeadColumns} />
                      </Table.Head>
                      <Table.Body>
                        {companyStaffsQuery.data?.map((columns, index) => {
                          return (
                            <MemberStaffDisplayEditRow
                              key={index}
                              index={index}
                              item={columns}
                              editing={editingList.includes(index)}
                              companyId={search.id}
                              onEditingChange={(editing) => {
                                if (editing) {
                                  setEditingList([...editingList, index]);
                                } else {
                                  setEditingList(editingList.filter((c) => c !== index));
                                }
                              }}
                            />
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </Collapse>
                </div>

                <FormTitle
                  title={strings.agencyInfo}
                  noEdit
                  noExpand={false}
                  expanded={expandAgencyInfo}
                  onChangeExpanded={(expanded) => {
                    setExpandAgencyInfo(expanded);
                  }}
                />
                <div className={classes.formContainer}>
                  <Collapse in={expandAgencyInfo} timeout="auto" unmountOnExit>
                    <Table>
                      <Table.Head>
                        <Table.Row columns={agencyHeadColumns} />
                      </Table.Head>
                      <Table.Body>
                        {agencyRows?.map((columns, index) => {
                          return <Table.Row key={index} columns={columns} />;
                        })}
                      </Table.Body>
                    </Table>
                    {companyRoutePlanList.data?.length === 0 && (
                      <div className={classes.emptyPrompt}>{strings.agenciesAreNotAssigned}</div>
                    )}
                  </Collapse>
                </div>
              </div>
              <div className={classes.rightColumn}>
                <InfoManagement id={search.id} type={type} />
              </div>
            </div>
          </MemberDetailsContext.Provider>
        );
      }}
    </Layout>
  );
};

export const meta: PageMeta = {
  title: strings.title,
  listed: false,
};
